import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { feed } from '../lib/api';
import { Heart, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BrandLogoWithWordmark from '../components/BrandLogo';

/**
 * Public post permalink — no login required.
 * Accessible at /post/:postId
 */
const PostPublicPage = () => {
  const { t, i18n } = useTranslation();
  const { postId } = useParams();
  const [post, setPost]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    feed.getPublicPost(postId)
      .then((res) => setPost(res.data))
      .catch(() => setError(t('postPublic.notFoundOrUnavailable')))
      .finally(() => setLoading(false));
  }, [postId, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-8 text-center">
        <p className="text-text-muted text-lg">{error || t('postPublic.notFound')}</p>
        <Link to="/login" className="text-blue-600 underline text-sm">{t('postPublic.signInToApp')}</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Branding */}
        <div className="mb-8 text-center">
          <Link to="/login" className="inline-flex justify-center">
            <BrandLogoWithWordmark size={40} />
          </Link>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          {/* Author */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium">
              {post.user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <p className="font-medium">{post.user?.name || 'Unknown'}</p>
              <p className="text-xs text-slate-400">
                {new Date(post.created_at).toLocaleDateString(
                  i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'fr' ? 'fr-FR' : 'en-GB',
                )}
                {post.content_type === 'announcement' && (
                  <span className="ml-2 bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-xs">📢 {t('feed.type_announcement')}</span>
                )}
              </p>
            </div>
          </div>

          <h1 className="text-2xl font-semibold mb-3">{post.title}</h1>
          {post.description && <p className="text-slate-600 mb-4 leading-relaxed">{post.description}</p>}

          {post.image_urls?.length > 0 && (
            <img src={post.image_urls[0]} alt="Post" className="w-full rounded-lg mb-4 object-cover max-h-96" />
          )}

          <div className="flex items-center gap-5 pt-4 border-t border-slate-100 text-text-muted">
            <span className="flex items-center gap-1.5"><Heart size={18} /> {post.likes?.length || 0}</span>
            <span className="flex items-center gap-1.5"><MessageSquare size={18} /> {post.comments?.length || 0}</span>
          </div>

          {/* Comments */}
          {post.comments?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100 space-y-3">
              <p className="text-sm font-medium text-slate-700">{t('postPublic.comments')}</p>
              {post.comments.map((c) => (
                <div key={c.id} className="flex gap-2">
                  <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center text-xs font-medium shrink-0">
                    {c.user?.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <span className="text-xs font-medium mr-2">{c.user?.name}</span>
                    <span className="text-sm text-slate-600">{c.content}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-center mt-6 text-sm text-slate-400">
          <Link to="/login" className="text-blue-500 hover:underline">{t('postPublic.signInToApp')}</Link> {t('postPublic.toComment')}
        </p>
      </div>
    </div>
  );
};

export default PostPublicPage;
